import { getPlatform, isB2B } from '@/domains/core/settings/utils';
import { useAuth } from '@/domains/customerManagement/auth/hooks/useAuth';
import { CouponContextType } from '@/domains/loyalty/components/Coupon/CouponContext';
import { useHighlightedCouponQuery } from '@/domains/loyalty/modules/HighlightedCoupon/generated/highlightedCoupon.query.generated';
import { Market, Platform } from '@/generated/graphql/types';

type HighlightedCoupon =
  | Record<string, never>
  | ({
      label: string;
      title: string;
      description: string;
    } & CouponContextType);

export const useHighlightedCoupon = (): HighlightedCoupon => {
  const platform = getPlatform().toUpperCase() as Platform;
  const market = isB2B() ? Market.B2B : Market.B2C;

  const {
    b2b_suspected_pro,
    b2b_intention,
    app_cluster,
    buy_number_cluster,
    customer_value,
    hasRetrievedAuthStatus,
    isAuthenticated,
    hasRetrievedCustomerSegments,
  } = useAuth();

  const [{ data }] = useHighlightedCouponQuery({
    requestPolicy: 'cache-first',
    variables: {
      platform,
      market,
      userSegments: {
        b2b_suspected_pro,
        b2b_intention,
        app_cluster,
        buy_number_cluster,
        customer_value,
      },
    },
    pause:
      !hasRetrievedAuthStatus ||
      (isAuthenticated && !hasRetrievedCustomerSegments),
  });

  if (
    !data?.thresholdHighlightedCoupons ||
    !data.thresholdHighlightedCoupons.coupons?.length
  )
    return {};

  return {
    label: data.thresholdHighlightedCoupons.label || '',
    title: data.thresholdHighlightedCoupons.title || '',
    description: data.thresholdHighlightedCoupons.description || '',
    code: data.thresholdHighlightedCoupons.coupons[0].code,
    amount: !data.thresholdHighlightedCoupons.coupons[0].isPercentage
      ? data.thresholdHighlightedCoupons.coupons[0].value
      : undefined,
    percent: data.thresholdHighlightedCoupons.coupons[0].isPercentage
      ? data.thresholdHighlightedCoupons.coupons[0].value
      : undefined,
    minPurchase: data.thresholdHighlightedCoupons.coupons[0].minPrice,
    validUntil: data.thresholdHighlightedCoupons.coupons[0].expirationDate,
    context: 'highlighted_coupon',
  };
};
